import React, {
  createContext,
  useContext,
  useState,
  useEffect,
  useCallback,
} from 'react'
import { transactionsService } from '../services/transactionsService'
import type {
  TransactionFilters,
  TransactionSortBy,
  AnalysisData,
  TransactionUI,
} from '@popety-io/types'

interface TransactionsContextState {
  transactions: TransactionUI[]
  loading: boolean
  error: string | null
  selectedTransaction: TransactionUI | null
  filters: TransactionFilters
  sortBy: TransactionSortBy
  total: number
  currentPage: number
  hasMore: boolean
  viewMode: 'list' | 'detail'
}

interface TransactionsContextValue extends TransactionsContextState {
  searchTransactions: (page?: number, append?: boolean) => Promise<void>
  loadMore: () => Promise<void>
  selectTransaction: (transaction: TransactionUI | null) => void
  updateFilters: (filters: TransactionFilters) => void
  clearFilters: () => void
  updateSort: (sortBy: TransactionSortBy) => void
  setViewMode: (mode: 'list' | 'detail') => void
  retrySearch: () => Promise<void>
  getLocationText: () => string
  getTimeframeText: () => string
}

const TransactionsContext = createContext<TransactionsContextValue | undefined>(
  undefined
)

export const useTransactions = () => {
  const context = useContext(TransactionsContext)
  if (!context) {
    throw new Error('useTransactions must be used within TransactionsProvider')
  }
  return context
}

interface TransactionsProviderProps {
  children: React.ReactNode
  analysisData?: AnalysisData | null
  propertyCoordinates?: [number, number]
  initialFilters?: Partial<TransactionFilters>
}

export const TransactionsProvider: React.FC<TransactionsProviderProps> = ({
  children,
  propertyCoordinates,
  initialFilters = {},
}) => {
  const defaultFilters: TransactionFilters = {
    location: 'within 500m',
    radius: 500,
    year: 2,
    reason: 'all',
    propertyType: 'all',
    municipality: [],
    postalCodes: [],
    ...initialFilters,
  }

  const [state, setState] = useState<TransactionsContextState>({
    transactions: [],
    loading: false,
    error: null,
    selectedTransaction: null,
    filters: defaultFilters,
    sortBy: 'recent',
    total: 0,
    currentPage: 1,
    hasMore: false,
    viewMode: 'list',
  })

  const pageSize = 20

  // Search transactions with current filters
  const searchTransactions = useCallback(
    async (page: number = 1) => {
      setState((prev) => ({ ...prev, loading: true, error: null }))

      try {
        const searchParams: any = {
          filters: state.filters,
          sortBy: state.sortBy,
          page,
          pageSize,
          propertyCoordinates,
        }

        const result = await transactionsService.search(searchParams)

        setState((prev) => ({
          ...prev,
          transactions: result.transactions,
          total: result.total,
          hasMore: result.hasMore,
          currentPage: page,
          loading: false,
        }))
      } catch (err) {
        console.error('Error searching transactions:', err)
        const errorMessage = 'Failed to load transactions'

        setState((prev) => ({
          ...prev,
          error: errorMessage,
          loading: false,
        }))
      }
    },
    [state.filters, state.sortBy, propertyCoordinates]
  )

  // Load more transactions
  const loadMore = useCallback(async () => {
    if (!state.loading && state.has_more) {
      await searchTransactions(state.currentPage + 1)
    }
  }, [searchTransactions, state.loading, state.has_more, state.currentPage])

  // Select transaction for detail view
  const selectTransaction = useCallback(
    async (transaction: TransactionUI | null) => {
      if (transaction) {
        // Try to load full transaction details
        try {
          const fullTransaction = await transactionsService.getById(
            transaction.id
          )

          if (fullTransaction) {
            setState((prev) => ({
              ...prev,
              selectedTransaction: fullTransaction,
              viewMode: 'detail',
            }))
          } else {
            console.log(
              'TransactionsContext: No full details found, using original transaction'
            )
            setState((prev) => ({
              ...prev,
              selectedTransaction: transaction,
              viewMode: 'detail',
            }))
          }
        } catch (err) {
          console.error(
            'TransactionsContext: Error loading transaction details:',
            err
          )
          // Use the original transaction data as fallback
          setState((prev) => ({
            ...prev,
            selectedTransaction: transaction,
            viewMode: 'detail',
          }))
        }
      } else {
        setState((prev) => ({
          ...prev,
          selectedTransaction: null,
          viewMode: 'list',
        }))
      }
    },
    []
  )

  // Update filters
  const updateFilters = useCallback((filters: TransactionFilters) => {
    setState((prev) => ({
      ...prev,
      filters,
      currentPage: 1, // Reset to first page when filters change
    }))
  }, [])

  // Clear filters
  const clearFilters = useCallback(() => {
    setState((prev) => ({
      ...prev,
      filters: defaultFilters,
      currentPage: 1,
    }))
  }, [])

  // Update sort
  const updateSort = useCallback((sortBy: TransactionSortBy) => {
    setState((prev) => ({
      ...prev,
      sortBy,
      currentPage: 1, // Reset to first page when sort changes
    }))
  }, [])

  // Set view mode
  const setViewMode = useCallback((mode: 'list' | 'detail') => {
    setState((prev) => ({ ...prev, viewMode: mode }))
  }, [])

  // Retry search
  const retrySearch = useCallback(async () => {
    await searchTransactions(1)
  }, [searchTransactions])

  // Get location display text
  const getLocationText = useCallback(() => {
    if (state.filters.municipality && state.filters.municipality.length > 0) {
      return state.filters.municipality.join(', ')
    }
    if (state.filters.postalCodes && state.filters.postalCodes.length > 0) {
      return state.filters.postalCodes.join(', ')
    }
    if (state.filters.radius) {
      return `within ${state.filters.radius}m`
    }
    return state.filters.location || 'within 500m'
  }, [state.filters])

  // Get timeframe display text
  const getTimeframeText = useCallback(() => {
    if (state.filters.year === 'all') return 'of all time'
    if (state.filters.year === 1) return 'in the last year'
    return `in the last ${state.filters.year} years`
  }, [state.filters.year])

  // Search when filters or sort changes
  useEffect(() => {
    searchTransactions(1)
  }, [state.filters, state.sortBy])

  const value: TransactionsContextValue = {
    ...state,
    searchTransactions,
    loadMore,
    selectTransaction,
    updateFilters,
    clearFilters,
    updateSort,
    setViewMode,
    retrySearch,
    getLocationText,
    getTimeframeText,
  }

  return (
    <TransactionsContext.Provider value={value}>
      {children}
    </TransactionsContext.Provider>
  )
}

export default TransactionsProvider
